import type { VideoResourceField } from '@src/services/VideoResourcesProvider';
import type WaveformData from 'waveform-data';

export enum VideoResolution {
  High = 'high',
  Low = 'low',
  Medium = 'medium',
  HD = 'hd',
  Original = 'original',
}

export enum AspectRatio {
  AR_16_9 = '16:9',
  AR_1_1 = '1:1',
  AR_9_16 = '9:16',
}

export enum AdditionalAspectRatio {
  AR_4_5 = '4:5',
}

export type ExtendedAspectRatio = AspectRatio | AdditionalAspectRatio;

export enum VideoDimensionsWidth {
  AR_16_9 = 1280,
  AR_1_1 = 720,
  AR_9_16 = 405,
  AR_4_5 = 576,
}

export enum VideoDimensionsHeight {
  AR_16_9 = 720,
  AR_1_1 = 720,
  AR_9_16 = 720,
  AR_4_5 = 720,
}

export enum TranscriptionStatus {
  Idle = 'idle',
  None = 'none',
  Loading = 'loading',
  Failed = 'failed',
  Loaded = 'loaded',
  GenerationFailed = 'generation_failed',
}

export enum SubtitleStatus {
  None = 'none',
  Loading = 'loading',
  Loaded = 'loaded',
  Failed = 'failed',
}

export enum VideoStatus {
  None = 'none',
  Rendering = 'rendering',
  Rendered = 'rendered',
  Editing = 'editing',
  Error = 'error',
}

/**
 * Content Studio clip review workflow status. Independent of the `isClientReady`
 * "Approved" toggle. Stored in the Dato `review_status` field; an empty/unset
 * value is treated as `New` (the default). String values are the persisted Dato
 * values and must not change.
 */
export enum ClipReviewStatus {
  New = 'New',
  Working = 'Working',
  NeedsReview = 'Needs Review',
  Approved = 'Approved',
}

/** Status for a clip with no stored `review_status` (empty/unset = New). */
export const DEFAULT_CLIP_REVIEW_STATUS: ClipReviewStatus =
  ClipReviewStatus.New;

/** All statuses in workflow order (`New` first); used for filters and option lists. */
export const CLIP_REVIEW_STATUSES: ClipReviewStatus[] = [
  ClipReviewStatus.New,
  ClipReviewStatus.Working,
  ClipReviewStatus.NeedsReview,
  ClipReviewStatus.Approved,
];

export type VideoResource = {
  url: string;
};

export type VideoResourceMetadata = {
  fileKey: string;
  fileName: string;
  md5Hash: string;
};

/**
 * PLAT-10788 — karaoke is stored per language: a map of BCP-47 code → resource.
 * A legacy flat pointer still validates via the union below; new writes emit the
 * map. Mirrors `arbor-v1/types/video.ts`.
 */
export type KaraokeLanguageMetadata = Record<string, VideoResourceMetadata>;

export type VideoResources = {
  videoSourceSnapshot?: VideoResource;
  transcriptionSnapshot?: VideoResource;
  // PLAT-10788 — a legacy flat pointer resolves to `{ url }`; a per-language map
  // resolves to `{ [lang]: { url } }` (mirrors the backend GET response).
  karaokeData?: VideoResource | Record<string, VideoResource>;
  scenes?: VideoResource;
};

export type VideoResourcesMetadata = Partial<
  Record<Exclude<VideoResourceField, 'karaokeData'>, VideoResourceMetadata>
> & {
  karaokeData?: VideoResourceMetadata | KaraokeLanguageMetadata;
};

export type VideoSource = Record<string, any>;

export type WaveformState =
  | { status: 'loading'; data: null }
  | { status: 'ready'; data: WaveformData }
  | { status: 'error'; data: null };
